import type { MarketingInspirationItem } from './types';

const normalizeText = (value?: string | null): string => String(value || '').trim();

export const buildMarketingInspirationContext = (items: MarketingInspirationItem[]): string => {
  const grouped = {
    scene: items.filter(item => item.type === 'scene'),
    quote: items.filter(item => item.type === 'quote'),
    vibe: items.filter(item => item.type === 'vibe'),
  };

  const sections: string[] = [];

  if (grouped.scene.length) {
    sections.push(
      [
        'Scene inspiration:',
        ...grouped.scene.map(item => {
          const parts = [normalizeText(item.title), normalizeText(item.text)];
          if (item.timePeriods?.length) {
            parts.push(`Time periods: ${item.timePeriods.join(', ')}`);
          }
          if (item.characterNames?.length) {
            parts.push(`Characters: ${item.characterNames.join(', ')}`);
          }
          if (item.locationNames?.length) {
            parts.push(`Locations: ${item.locationNames.join(', ')}`);
          }
          if (item.vibeTags?.length) {
            parts.push(`Vibes: ${item.vibeTags.join(', ')}`);
          }
          if (normalizeText(item.sceneVideo?.prompt)) {
            parts.push(`10-12s video: ${normalizeText(item.sceneVideo?.prompt)}`);
          }
          if (item.sceneVideo?.beats?.length) {
            parts.push(`Beats: ${item.sceneVideo.beats.join(' -> ')}`);
          }
          if (item.sceneVideo?.dialogueOverlayLines?.length) {
            parts.push(`Dialogue overlay: ${item.sceneVideo.dialogueOverlayLines.join(' | ')}`);
          }
          if (normalizeText(item.usageHints?.characterStudio)) {
            parts.push(`Still-image use: ${normalizeText(item.usageHints?.characterStudio)}`);
          }
          if (normalizeText(item.usageHints?.videoStudio)) {
            parts.push(`Motion/copy use: ${normalizeText(item.usageHints?.videoStudio)}`);
          }
          if (normalizeText(item.usageHints?.socialSlides)) {
            parts.push(`Social-slide use: ${normalizeText(item.usageHints?.socialSlides)}`);
          }
          if (normalizeText(item.sourceExcerpt)) {
            parts.push(`Source excerpt: ${normalizeText(item.sourceExcerpt)}`);
          }
          return `- ${parts.filter(Boolean).join(' | ')}`;
        }),
      ].join('\n')
    );
  }

  if (grouped.quote.length) {
    sections.push(
      [
        'Quote inspiration:',
        ...grouped.quote.map(item => {
          const parts = [
            normalizeText(item.title),
            normalizeText(item.quoteText) ? `Quote: ${normalizeText(item.quoteText)}` : '',
            normalizeText(item.text) ? `Use: ${normalizeText(item.text)}` : '',
            item.timePeriods?.length ? `Time periods: ${item.timePeriods.join(', ')}` : '',
            item.characterNames?.length ? `Characters: ${item.characterNames.join(', ')}` : '',
            item.tags?.length ? `Tags: ${item.tags.join(', ')}` : '',
            item.vibeTags?.length ? `Vibes: ${item.vibeTags.join(', ')}` : '',
            normalizeText(item.usageHints?.characterStudio)
              ? `Still-image use: ${normalizeText(item.usageHints?.characterStudio)}`
              : '',
            normalizeText(item.usageHints?.videoStudio)
              ? `Motion/copy use: ${normalizeText(item.usageHints?.videoStudio)}`
              : '',
            normalizeText(item.usageHints?.socialSlides)
              ? `Social-slide use: ${normalizeText(item.usageHints?.socialSlides)}`
              : '',
            normalizeText(item.sourceExcerpt) ? `Source excerpt: ${normalizeText(item.sourceExcerpt)}` : '',
          ];
          return `- ${parts.filter(Boolean).join(' | ')}`;
        }),
      ].join('\n')
    );
  }

  if (grouped.vibe.length) {
    sections.push(
      [
        'Vibe inspiration:',
        ...grouped.vibe.map(item => {
          const parts = [normalizeText(item.title), normalizeText(item.text)];
          if (item.timePeriods?.length) {
            parts.push(`Time periods: ${item.timePeriods.join(', ')}`);
          }
          if (item.characterNames?.length) {
            parts.push(`Characters: ${item.characterNames.join(', ')}`);
          }
          if (item.locationNames?.length) {
            parts.push(`Locations: ${item.locationNames.join(', ')}`);
          }
          if (item.tags?.length) {
            parts.push(`Tags: ${item.tags.join(', ')}`);
          }
          if (item.vibeTags?.length) {
            parts.push(`Vibes: ${item.vibeTags.join(', ')}`);
          }
          if (normalizeText(item.usageHints?.characterStudio)) {
            parts.push(`Still-image use: ${normalizeText(item.usageHints?.characterStudio)}`);
          }
          if (normalizeText(item.usageHints?.videoStudio)) {
            parts.push(`Motion/copy use: ${normalizeText(item.usageHints?.videoStudio)}`);
          }
          if (normalizeText(item.usageHints?.socialSlides)) {
            parts.push(`Social-slide use: ${normalizeText(item.usageHints?.socialSlides)}`);
          }
          if (normalizeText(item.sourceExcerpt)) {
            parts.push(`Source excerpt: ${normalizeText(item.sourceExcerpt)}`);
          }
          return `- ${parts.filter(Boolean).join(' | ')}`;
        }),
      ].join('\n')
    );
  }

  return sections.filter(Boolean).join('\n\n').trim();
};

export const sortMarketingInspirationItems = (
  items: MarketingInspirationItem[]
): MarketingInspirationItem[] =>
  [...items].sort((left, right) => {
    const leftApproved = normalizeText(left.status).toLowerCase() === 'approved' ? 0 : 1;
    const rightApproved = normalizeText(right.status).toLowerCase() === 'approved' ? 0 : 1;
    if (leftApproved !== rightApproved) {
      return leftApproved - rightApproved;
    }
    const leftRecommended = left.recommended ? 0 : 1;
    const rightRecommended = right.recommended ? 0 : 1;
    if (leftRecommended !== rightRecommended) return leftRecommended - rightRecommended;
    const leftRank = Number(left.recommendationRank || Number.MAX_SAFE_INTEGER);
    const rightRank = Number(right.recommendationRank || Number.MAX_SAFE_INTEGER);
    if (leftRank !== rightRank) return leftRank - rightRank;
    return normalizeText(right.updatedAt || right.createdAt).localeCompare(
      normalizeText(left.updatedAt || left.createdAt)
    );
  });

export const buildMarketingInspirationMismatchWarning = ({
  selectedCharacterId,
  selectedCharacterName,
  items,
}: {
  selectedCharacterId?: string;
  selectedCharacterName?: string;
  items: MarketingInspirationItem[];
}): string => {
  const normalizedCharacterId = normalizeText(selectedCharacterId).toLowerCase();
  const normalizedCharacterName = normalizeText(selectedCharacterName).toLowerCase();
  if (!normalizedCharacterId && !normalizedCharacterName) {
    return '';
  }
  const mismatched = items.filter(item => {
    const itemCharacterIds = (item.characterIds || []).map(value => normalizeText(value).toLowerCase()).filter(Boolean);
    const itemCharacterNames = (item.characterNames || []).map(value => normalizeText(value).toLowerCase()).filter(Boolean);
    if (!itemCharacterIds.length && !itemCharacterNames.length) {
      return false;
    }
    if (normalizedCharacterId && itemCharacterIds.includes(normalizedCharacterId)) {
      return false;
    }
    if (normalizedCharacterName && itemCharacterNames.includes(normalizedCharacterName)) {
      return false;
    }
    return true;
  });
  if (!mismatched.length) {
    return '';
  }
  const labels = mismatched
    .map(item => normalizeText(item.title) || normalizeText(item.quoteText) || 'Untitled inspiration')
    .filter(Boolean)
    .slice(0, 3);
  const subject = selectedCharacterName || 'the selected character';
  return `Some selected inspiration references different characters than ${subject}: ${labels.join(', ')}.`;
};
